import type { AppRouter } from "@newform-monorepo/backend/src/router";
import { TRPCClientError, type TRPCLink } from "@trpc/client";
import { observable } from "@trpc/server/observable";

export function isSessionExpiredError(error: Error): boolean {
  return (
    error instanceof TRPCClientError &&
    error.data?.code === "UNAUTHORIZED" &&
    error.data.sessionExpired === true
  );
}

/**
 * Whether the API has said this tab's sign-in ended. The session cookie is
 * HttpOnly, so the API's answer is the only signal the browser gets.
 */
export function createSessionRecovery() {
  let ended = false;
  const listeners = new Set<() => void>();
  return {
    getSnapshot: () => ended,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    report() {
      if (ended) return;
      ended = true;
      for (const listener of listeners) listener();
    },
  };
}

export type SessionRecovery = ReturnType<typeof createSessionRecovery>;

export function sessionRecoveryLink(
  recovery: SessionRecovery,
): TRPCLink<AppRouter> {
  return () =>
    ({ op, next }) =>
      observable((observer) =>
        next(op).subscribe({
          next(value) {
            observer.next(value);
          },
          error(error) {
            // Streamed batches can return HTTP 200 with a failed operation.
            // Only the auth middleware's marker means the login is invalid;
            // a password/login procedure can also return UNAUTHORIZED.
            if (
              error.data?.code === "UNAUTHORIZED" &&
              error.data.sessionExpired
            ) {
              recovery.report();
            }
            observer.error(error);
          },
          complete() {
            observer.complete();
          },
        }),
      );
}
